import React, { useEffect, useMemo, useRef, useState } from "react";
import "antd/dist/antd.css";
import { Select } from "antd";

const FUEL_TYPE_LABELS = ["Petrol", "Diesel", "Hybrid", "EV"];
const FUEL_ALIASES = {
  petrol: "Petrol",
  gasoline: "Petrol",
  diesel: "Diesel",
  hybrid: "Hybrid",
  ev: "EV",
  electric: "EV",
  "electric vehicle": "EV",
};

function normalizeFuelLabel(raw) {
  const key = String(raw || "").trim().toLowerCase();
  return FUEL_ALIASES[key] || null;
}

/**
 * Custom listbox for dashboard forms — avoids the native OS <select>
 * flash (empty gray popup, then options) on Linux/Chrome.
 */
function DashboardSelect({
  label,
  required,
  invalid,
  errorText,
  selectedKey,
  placeholder,
  disabled,
  loading,
  options,
  onPick,
}) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      setOpen(false);
      setQuery("");
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) =>
      String(opt.label).toLowerCase().includes(q)
    );
  }, [options, query]);

  const showSearch = options.length > 8;
  const isDisabled = Boolean(disabled || loading);

  return (
    <div
      className={`dd-field dd-select${invalid ? " is-invalid" : ""}${
        open ? " is-open" : ""
      }`}
      ref={rootRef}
    >
      <label>
        {label}
        {required ? (
          <span className="dd-field__required" aria-hidden>
            {" "}
            *
          </span>
        ) : null}
      </label>
      <button
        type="button"
        className={`dd-select__trigger${selectedKey ? "" : " is-placeholder"}`}
        disabled={isDisabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        aria-invalid={invalid ? "true" : "false"}
        onClick={() => {
          if (isDisabled) return;
          setOpen((prev) => !prev);
          setQuery("");
        }}
      >
        <span>{selectedKey || placeholder}</span>
        <span className="dd-select__chevron" aria-hidden />
      </button>
      {open ? (
        <div className="dd-select__panel" role="listbox" aria-label={label}>
          {showSearch ? (
            <div className="dd-select__search">
              <input
                type="search"
                value={query}
                autoFocus
                placeholder={`Search ${label.toLowerCase()}…`}
                onChange={(e) => setQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          ) : null}
          {loading ? (
            <div className="dd-select__empty">Loading {label}…</div>
          ) : filtered.length === 0 ? (
            <div className="dd-select__empty">
              {options.length === 0
                ? `No ${label.toLowerCase()} options`
                : "No matches"}
            </div>
          ) : (
            <ul className="dd-select__list">
              {filtered.map((opt) => {
                const active = String(opt.value) === String(selectedKey);
                return (
                  <li key={opt.key}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={active}
                      className={`dd-select__option${
                        active ? " is-active" : ""
                      }`}
                      onClick={() => {
                        onPick(opt.value);
                        setOpen(false);
                        setQuery("");
                      }}
                    >
                      {opt.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}
      {invalid && errorText ? (
        <p className="dd-password__error" role="alert">
          {errorText}
        </p>
      ) : null}
    </div>
  );
}

export default function Dropdown(props) {
  const {
    label,
    data,
    onSelect,
    titleKey,
    idKey,
    disabled,
    loading,
    selectedValue,
    restrictFuelType,
    variant,
    required,
    invalid,
    errorText,
  } = props;

  const { Option } = Select;
  const titleKey_ = titleKey || "title";
  const idKey_ = idKey || "id";
  const isDashboard = variant === "dashboard";

  const sourceData = Array.isArray(data) ? data : [];
  const displayData = restrictFuelType
    ? sourceData.reduce((list, item) => {
        const labelText = normalizeFuelLabel(item?.[titleKey_]);
        if (!labelText) return list;
        if (list.some((entry) => String(entry[titleKey_]) === labelText)) {
          return list;
        }
        list.push({ ...item, [titleKey_]: labelText });
        return list;
      }, [])
    : sourceData;

  function resolveItem(value) {
    return (
      displayData.find((item) => String(item[titleKey_]) === String(value)) ||
      sourceData.find((item) => String(item[titleKey_]) === String(value)) ||
      null
    );
  }

  function onSelectHandler(value) {
    const selectedItem = resolveItem(value);
    if (!selectedItem) return;
    onSelect(selectedItem);
  }

  useEffect(() => {
    if (displayData.length === 1 && !selectedValue?.[idKey_]) {
      onSelectHandler(displayData[0][titleKey_]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const selectedLabel = restrictFuelType
    ? normalizeFuelLabel(selectedValue?.[titleKey_]) ||
      selectedValue?.[titleKey_]
    : selectedValue?.[titleKey_];
  let selectedKey =
    selectedLabel != null && selectedLabel !== ""
      ? String(selectedLabel)
      : "";
  // Fall back to matching by id when the selected object uses a different label shape.
  if (!selectedKey && selectedValue) {
    const selectedId = selectedValue?.[idKey_];
    if (selectedId != null && selectedId !== "") {
      const matched =
        displayData.find(
          (item) => String(item[idKey_]) === String(selectedId)
        ) ||
        sourceData.find((item) => String(item[idKey_]) === String(selectedId));
      if (matched?.[titleKey_] != null) selectedKey = String(matched[titleKey_]);
    }
  }
  // Brand rows use `id` while model/fuel/year use `value`.
  if (!selectedKey && selectedValue?.id != null && idKey_ !== "id") {
    const matched =
      displayData.find((item) => String(item.id) === String(selectedValue.id)) ||
      sourceData.find((item) => String(item.id) === String(selectedValue.id));
    if (matched?.[titleKey_] != null) selectedKey = String(matched[titleKey_]);
  }

  if (isDashboard) {
    const options = displayData
      .map((item, index) => {
        const value = item[titleKey_];
        if (value == null || value === "") return null;
        return {
          key: `${item[idKey_] ?? value ?? index}`,
          value: String(value),
          label: String(value),
        };
      })
      .filter(Boolean);

    return (
      <DashboardSelect
        label={label}
        required={required}
        invalid={invalid}
        errorText={errorText}
        selectedKey={selectedKey}
        placeholder={loading ? `Loading ${label}…` : `Select ${label}`}
        disabled={disabled}
        loading={loading}
        options={options}
        onPick={onSelectHandler}
      />
    );
  }

  const children = displayData.map((item, index) => (
    <Option
      key={`${item[idKey_] ?? item[titleKey_] ?? index}`}
      value={item[titleKey_]}
      label={String(item[titleKey_] ?? "")}
    >
      {item[titleKey_]}
    </Option>
  ));

  return (
    <div className="dropdown_wrapper1">
      <div style={{ fontWeight: "bold", marginBottom: 5 }}>{label}:</div>
      <Select
        loading={loading}
        disabled={disabled}
        showSearch
        style={{ width: "100%" }}
        placeholder={`${loading ? "Loading" : "Select"} ${label}`}
        onChange={onSelectHandler}
        value={selectedLabel || undefined}
        optionFilterProp="label"
        filterOption={(input, option) =>
          String(option?.label ?? option?.value ?? "")
            .toLowerCase()
            .includes(input.toLowerCase())
        }
      >
        {children}
      </Select>
    </div>
  );
}
